import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Send, CheckCircle, FileDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import SEO from '../components/SEO';
import { BUSINESS_PHONE_DISPLAY, BUSINESS_PHONE_TEL, LEGAL_CONSENT_DOCUMENT_PATH } from '../constants/site';
import { trackMetaLead } from '../utils/metaPixel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    business_name: '',
    asset_type: '',
    loan_amount: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (value) => {
    setFormData(prev => ({ ...prev, asset_type: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const payload = {
        ...formData,
        loan_amount: formData.loan_amount ? parseFloat(formData.loan_amount) : null
      };
      
      await axios.post(`${API}/enquiry`, payload);

      trackMetaLead({
        content_name: 'Contact Enquiry Submitted',
        content_category: 'contact_enquiry',
      });

      setIsSubmitted(true);
      toast.success('Enquiry submitted successfully! We\'ll be in touch soon.');
    } catch (error) {
      console.error('Error submitting enquiry:', error);
      toast.error('Something went wrong. Please try again or call us directly.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen pt-20">
        <section className="py-20 lg:py-28 bg-navy-900">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-8">
              <CheckCircle size={40} className="text-green-500" />
            </div>
            <h1 className="font-heading font-black text-4xl sm:text-5xl text-white uppercase tracking-tight mb-6">
              Thank You!
            </h1>
            <p className="font-body text-lg text-white/70 mb-8">
              Your enquiry has been received. One of our finance specialists will be in touch 
              within 24 hours to discuss your needs.
            </p>
            <p className="font-body text-white/60 mb-8">
              Need to speak to someone urgently? Call us on{' '}
              <a href={BUSINESS_PHONE_TEL} className="text-gold-500 hover:underline font-mono">{BUSINESS_PHONE_DISPLAY}</a>
            </p>
            <Button
              onClick={() => {
                setIsSubmitted(false);
                setFormData({
                  name: '',
                  email: '',
                  phone: '',
                  business_name: '',
                  asset_type: '',
                  loan_amount: '',
                  message: ''
                });
              }}
              className="gold-gradient text-black font-heading font-bold uppercase tracking-wider"
            >
              Submit Another Enquiry
            </Button>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20">
      <SEO 
        title="Contact Flash Funding | Commercial Finance Broker"
        description="Get in touch with Flash Funding, Australia's commercial finance broker. Call 02 7238 6211 or send us an enquiry. Sydney, Melbourne, Brisbane and beyond."
        keywords="contact Flash Funding, commercial finance quote, equipment finance enquiry, business loan application, finance broker Sydney, get finance quote"
        canonical="/contact"
      />
      {/* Hero Section */}
      <section className="py-16 lg:py-20 bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-white tracking-tight leading-none mb-4" data-testid="contact-page-main-heading">
              Talk to a Finance Broker — We'll Find Your Best Option
            </h1>
            <p className="font-body text-lg text-white/70">
              Tell us what you need and we’ll guide you to the right path for Car Loans, Personal Loans, or Business Finance.
            </p>

            <div className="mt-7 flex flex-wrap justify-center gap-3" data-testid="contact-hero-cta-row">
              <Link to="/car-loans" data-testid="contact-hero-car-link">
                <Button className="rounded-full bg-purple-600 hover:bg-purple-500 text-white text-xs uppercase tracking-[0.13em] font-bold px-6 py-5" data-testid="contact-hero-car-cta">
                  Check Your Options
                </Button>
              </Link>
              <Link to="/commercial-car-loans" data-testid="contact-hero-commercial-car-link">
                <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 text-xs uppercase tracking-[0.13em] font-bold px-6 py-5" data-testid="contact-hero-commercial-car-cta">
                  Check Your Options
                </Button>
              </Link>
              <Link to="/personal-loans" data-testid="contact-hero-personal-link">
                <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 text-xs uppercase tracking-[0.13em] font-bold px-6 py-5" data-testid="contact-hero-personal-cta">
                  See What You Can Get Approved For
                </Button>
              </Link>
              <Link to="/business-finance" data-testid="contact-hero-business-link">
                <Button variant="outline" className="rounded-full border-purple-400/40 bg-navy-900/60 text-white hover:bg-purple-500/15 text-xs uppercase tracking-[0.13em] font-bold px-6 py-5" data-testid="contact-hero-business-cta">
                  Check Your Options
                </Button>
              </Link>
            </div>

            <p className="font-body text-sm text-white/60 mt-4" data-testid="contact-hero-route-priority-note">
              Start with the relevant product path, then continue in our secure customer portal when ready.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 lg:py-24 bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="space-y-8">
              <div>
                <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight mb-6">
                  Contact Details
                </h2>
                <ul className="space-y-6">
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                      <Phone size={22} className="text-gold-500" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-white uppercase text-sm mb-1">Phone</p>
                      <a href={BUSINESS_PHONE_TEL} className="font-mono text-lg text-gold-500 hover:underline" data-testid="contact-business-phone-link">
                        {BUSINESS_PHONE_DISPLAY}
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                      <Mail size={22} className="text-gold-500" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-white uppercase text-sm mb-1">Email</p>
                      <a href="mailto:hello@flashfunding.loan" className="font-body text-gold-500 hover:underline">
                        hello@flashfunding.loan
                      </a>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                      <MapPin size={22} className="text-gold-500" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-white uppercase text-sm mb-1">Locations</p>
                      <p className="font-body text-white/70">
                        Sydney • Melbourne • Brisbane<br />
                        Perth • Adelaide
                      </p>
                    </div>
                  </li>
                  <li className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-lg bg-gold-500/10 flex items-center justify-center flex-shrink-0">
                      <Clock size={22} className="text-gold-500" />
                    </div>
                    <div>
                      <p className="font-heading font-bold text-white uppercase text-sm mb-1">Business Hours</p>
                      <p className="font-body text-white/70">
                        Monday - Friday: 8am - 6pm<br />
                        Saturday: 9am - 1pm
                      </p>
                    </div>
                  </li>
                </ul>
              </div>

              {/* Quick Facts */}
              <div className="bg-navy-700 border border-white/5 p-6">
                <h3 className="font-heading font-bold text-lg text-white uppercase mb-4">Why Flash Funding?</h3>
                <ul className="space-y-3 font-body text-white/70 text-sm">
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-gold-500 rounded-full"></div>
                    Fast approvals — often within 24 hours, depending on the applicant, credit criteria and loan type
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-gold-500 rounded-full"></div>
                    We handle lender matching and process follow-up
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-gold-500 rounded-full"></div>
                    Access to 40+ lenders on panel
                  </li>
                  <li className="flex items-center gap-2">
                    <div className="w-1.5 h-1.5 bg-gold-500 rounded-full"></div>
                    Better approval positioning vs going direct
                  </li>
                </ul>
              </div>

              {/* Credit Guide Download */}
              <div className="bg-navy-700 border border-gold-500/20 p-6">
                <h3 className="font-heading font-bold text-lg text-white uppercase mb-3">Credit Guide</h3>
                <p className="font-body text-white/60 text-sm mb-4">
                  Before we provide credit assistance, please read our Credit Guide which contains important information about our services.
                </p>
                <a 
                  href={LEGAL_CONSENT_DOCUMENT_PATH}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 w-full justify-center px-4 py-3 bg-gold-500/10 border border-gold-500/30 text-gold-500 hover:bg-gold-500 hover:text-black transition-colors font-heading font-bold text-sm uppercase tracking-wider"
                  data-testid="contact-credit-guide-download"
                >
                  <FileDown size={18} />
                  Download Credit Guide & Privacy (PDF)
                </a>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-navy-700 border border-white/5 p-8 lg:p-10">
                <h2 className="font-heading font-bold text-2xl text-white uppercase tracking-tight mb-8">
                  Tell us what you need
                </h2>

                <p className="text-white/65 text-sm mb-6" data-testid="contact-form-clarity-text">
                  This form is for personal and business finance enquiries. We’ll review your details and recommend the best next step.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Name */}
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-body text-white/80">
                        Full Name <span className="text-gold-500">*</span>
                      </Label>
                      <Input
                        id="name"
                        name="name"
                        data-testid="contact-name-input"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="bg-navy-900 border-white/10 text-white h-12"
                        placeholder="John Smith"
                      />
                    </div>

                    {/* Email */}
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-body text-white/80">
                        Email Address <span className="text-gold-500">*</span>
                      </Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        data-testid="contact-email-input"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="bg-navy-900 border-white/10 text-white h-12"
                        placeholder="john@company.com.au"
                      />
                    </div>

                    {/* Phone */}
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="font-body text-white/80">
                        Phone Number <span className="text-gold-500">*</span>
                      </Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        data-testid="contact-phone-input"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="bg-navy-900 border-white/10 text-white h-12"
                        placeholder="0400 000 000"
                      />
                    </div>

                    {/* Business Name */}
                    <div className="space-y-2">
                      <Label htmlFor="business_name" className="font-body text-white/80">
                        Business Name
                      </Label>
                      <Input
                        id="business_name"
                        name="business_name"
                        data-testid="contact-business-input"
                        value={formData.business_name}
                        onChange={handleChange}
                        className="bg-navy-900 border-white/10 text-white h-12"
                        placeholder="Your Business Pty Ltd"
                      />
                    </div>

                    {/* Asset Type */}
                    <div className="space-y-2">
                      <Label className="font-body text-white/80">
                        Type of Finance <span className="text-gold-500">*</span>
                      </Label>
                      <Select value={formData.asset_type} onValueChange={handleSelectChange} required>
                        <SelectTrigger 
                          data-testid="contact-asset-select"
                          className="bg-navy-900 border-white/10 text-white h-12"
                        >
                          <SelectValue placeholder="Select finance type" />
                        </SelectTrigger>
                        <SelectContent className="bg-navy-800 border-white/10">
                          <SelectItem value="Equipment Finance" className="text-white hover:bg-navy-700">Equipment Finance</SelectItem>
                          <SelectItem value="Vehicle Finance" className="text-white hover:bg-navy-700">Vehicle Finance</SelectItem>
                          <SelectItem value="Business Loan" className="text-white hover:bg-navy-700">Business Loan</SelectItem>
                          <SelectItem value="Line of Credit" className="text-white hover:bg-navy-700">Line of Credit</SelectItem>
                          <SelectItem value="Other" className="text-white hover:bg-navy-700">Other / Not Sure</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Loan Amount */}
                    <div className="space-y-2">
                      <Label htmlFor="loan_amount" className="font-body text-white/80">
                        Estimated Amount ($)
                      </Label>
                      <Input
                        id="loan_amount"
                        name="loan_amount"
                        type="number"
                        data-testid="contact-amount-input"
                        value={formData.loan_amount}
                        onChange={handleChange}
                        className="bg-navy-900 border-white/10 text-white h-12"
                        placeholder="100000"
                      />
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-body text-white/80">
                      Tell us about your needs
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      data-testid="contact-message-input"
                      value={formData.message}
                      onChange={handleChange}
                      rows={4}
                      className="bg-navy-900 border-white/10 text-white resize-none"
                      placeholder="Describe what you're looking to finance, any specific requirements, or questions you have..."
                    />
                  </div>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    data-testid="contact-submit-btn"
                    disabled={isSubmitting}
                    className="w-full gold-gradient text-black font-heading font-bold text-lg uppercase tracking-wider py-6 hover:scale-[1.02] transition-transform gold-glow disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <>Processing...</>
                    ) : (
                      <>
                        Speak to a Finance Specialist
                        <Send size={20} className="ml-2" />
                      </>
                    )}
                  </Button>

                  <p className="font-body text-white/40 text-xs text-center">
                    By submitting this form, you agree to be contacted by Flash Funding regarding your enquiry. 
                    Your information is secure and will never be shared with third parties.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
